import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import api, { userAPI } from '../services/api';
import ForgotPasswordModal from './ForgotPasswordModal';
import VerifyEmailOtpModal from './VerifyEmailOtpModal';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (user: any) => void;
}

interface PendingOtp {
  token: string;
  email: string;
  userPayload: any;
  codeAlreadySent: boolean;
  destinationMasked?: string;
  expiresInMinutes?: number;
  resendAvailableInSeconds?: number;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  type: string;
  agreeToTerms: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  type?: string;
  agreeToTerms?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onAuthSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [pendingOtp, setPendingOtp] = useState<PendingOtp | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    type: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  if (!isOpen) return null;

  const finishAuth = (userPayload: any) => {
    onAuthSuccess(userPayload);
    onClose();
    setPendingOtp(null);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Sign up specific validations
    if (isSignUp) {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let response;

      if (isSignUp) {
        // Register new user
        response = await api.user.register({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          type: 'user'
        });
        if (response) {
          localStorage.setItem('piezit-user', JSON.stringify(response.user));
          localStorage.removeItem('piezit-organization');
          if (response.token) {
            localStorage.setItem('piezit-token', response.token);
          }
        }

        console.log('✅ Authentication successful: Registered');

        if (response?.requiresEmailVerification && response.token) {
          setPendingOtp({
            token: response.token,
            email: response.user?.email || formData.email,
            userPayload: response.user,
            codeAlreadySent: Boolean(response.otp),
            destinationMasked: response.otp?.destinationMasked,
            expiresInMinutes: response.otp?.expiresInMinutes,
            resendAvailableInSeconds: response.otp?.resendAvailableInSeconds,
          });
          return;
        }

        finishAuth(response.user);
        return;
      }

      // Login existing user
      response = await userAPI.login(formData.email, formData.password);
      console.log(response);

      if (response.type == 'user') {
        localStorage.setItem('piezit-user', JSON.stringify(response.user));
        localStorage.removeItem('piezit-organization');
        if (response.token) {
          localStorage.setItem('piezit-token', response.token);
        }

        console.log('✅ Authentication successful: Logged in');

        if (response.requiresEmailVerification && response.token) {
          setPendingOtp({
            token: response.token,
            email: response.user?.email || formData.email,
            userPayload: response.user,
            codeAlreadySent: false,
          });
          return;
        }

        finishAuth(response.user);
        return;
      }

      if (response.type == 'business') {
        localStorage.setItem('piezit-organization', JSON.stringify(response.org));
        localStorage.removeItem('piezit-user');
        if (response.token) {
          localStorage.setItem('piezit-org-token', response.token);
        }

        console.log('✅ Authentication successful: Business logged in');

        if (response.requiresEmailVerification && response.token) {
          setPendingOtp({
            token: response.token,
            email: response.org?.email || formData.email,
            userPayload: response.org,
            codeAlreadySent: false,
          });
          return;
        }

        finishAuth(response.org);
      }
    } catch (error: any) {
      setErrors({
        email: error.message || 'Authentication failed. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setErrors({});
    setFormData({
      email: formData.email,
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      type: 'user',
      agreeToTerms: false
    });
  };

  if (pendingOtp) {
    return (
      <VerifyEmailOtpModal
        isOpen
        email={pendingOtp.email}
        token={pendingOtp.token}
        codeAlreadySent={pendingOtp.codeAlreadySent}
        destinationMasked={pendingOtp.destinationMasked}
        expiresInMinutes={pendingOtp.expiresInMinutes}
        initialResendCooldownSeconds={pendingOtp.resendAvailableInSeconds}
        allowSkip
        onVerified={() => {
          const updated = { ...pendingOtp.userPayload, verified: true };
          if (updated.type === 'business' || localStorage.getItem('piezit-organization')) {
            localStorage.setItem('piezit-organization', JSON.stringify(updated));
          } else {
            localStorage.setItem('piezit-user', JSON.stringify(updated));
          }
          finishAuth(updated);
        }}
        onSkip={() => finishAuth(pendingOtp.userPayload)}
        onClose={() => finishAuth(pendingOtp.userPayload)}
      />
    );
  }

  if (showForgotPassword) {
    return (
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        initialEmail={formData.email}
        onClose={() => {
          setShowForgotPassword(false);
          onClose();
        }}
        onBackToLogin={() => setShowForgotPassword(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isSignUp ? 'Join Piezit' : 'Welcome Back'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {isSignUp && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`input-field pl-10 ${errors.firstName ? 'border-red-300' : ''}`}
                    placeholder="John"
                  />
                </div>
                {errors.firstName && (
                  <div className="flex items-center space-x-1 mt-1 text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    <span className="text-xs">{errors.firstName}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`input-field pl-10 ${errors.lastName ? 'border-red-300' : ''}`}
                    placeholder="Doe"
                  />
                </div>
                {errors.lastName && (
                  <div className="flex items-center space-x-1 mt-1 text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    <span className="text-xs">{errors.lastName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                placeholder="john@example.com"
              />
            </div>
            {errors.email && (
              <div className="flex items-center space-x-1 mt-1 text-red-600">
                <AlertCircle className="w-3 h-3" />
                <span className="text-xs">{errors.email}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-300' : ''}`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <div className="flex items-center space-x-1 mt-1 text-red-600">
                <AlertCircle className="w-3 h-3" />
                <span className="text-xs">{errors.password}</span>
              </div>
            )}
            {!isSignUp && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="flex items-center space-x-1 mt-1 text-red-600">
                  <AlertCircle className="w-3 h-3" />
                  <span className="text-xs">{errors.confirmPassword}</span>
                </div>
              )}
            </div>
          )}

          {isSignUp && (
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label htmlFor="agreeToTerms" className="text-sm text-gray-700 cursor-pointer">
                  I agree to the{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-700">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>
                </label>
                {errors.agreeToTerms && (
                  <div className="flex items-center space-x-1 mt-1 text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    <span className="text-xs">{errors.agreeToTerms}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
              </>
            )}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={toggleMode}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : "Don't have an account? Sign up"
              }
            </button>
          </div>
        </form>

        {/* Benefits for sign up */}
        {isSignUp && (
          <div className="px-6 pb-6">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-primary-900 mb-2">
                Join the Piezit Community
              </h3>
              <ul className="text-sm text-primary-800 space-y-1">
                <li>• Share unlimited positive reviews</li>
                <li>• Get verified reviewer badge</li>
                <li>• Build your reputation in the community</li>
                <li>• Access personalized recommendations</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
